/**
 * TaxonomyExplorer · Sprint T-5 · Canonical Knowledge Layer.
 *
 * Founder OS → Intelligence → Taxonomy Explorer
 *
 * Permite a la fundación visualizar TODO el árbol canónico v1.2:
 *   - Buscador (nombre, código, sinónimo) sobre el RAG taxonomy index
 *   - Árbol L1 navegable con drill-down a L2 → L3
 *   - Panel derecho con Knowledge Document completo del nodo seleccionado
 *   - Relaciones (alias_of, complementary, requires, precedes, deprecated_by)
 *   - Profesionales aplicables (P-01..P-24)
 *   - Stats globales + coverage (multi-dimension)
 *
 * Backend endpoints (read-only):
 *   - GET /api/taxonomy/tree                       · árbol completo
 *   - GET /api/taxonomy/search?q=                  · búsqueda fuzzy
 *   - GET /api/taxonomy/node/{code}                · nodo
 *   - GET /api/ai/knowledge/{code}                 · Knowledge Document rico
 *   - GET /api/taxonomy/coverage                   · métricas reales
 *   - GET /api/ai/rag/taxonomy/search?q=&top_k=    · RAG taxonomy
 */
import { useEffect, useMemo, useState } from "react";
import {
  TreeStructure, MagnifyingGlass, Graph, ChartBar, Books,
  ArrowRight, Hash, Link as LinkIcon, Tag, CaretRight, CaretDown,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";

export default function TaxonomyExplorer() {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState(new Set(["T-04"]));
  const [selectedCode, setSelectedCode] = useState("T-04");
  const [knowledge, setKnowledge] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [error, setError] = useState(null);

  // Load tree + coverage on mount
  useEffect(() => {
    (async () => {
      try {
        const [treeRes, covRes] = await Promise.all([
          api.get("/taxonomy/tree?max_level=3"),
          api.get("/taxonomy/coverage"),
        ]);
        setTree(treeRes.data?.items || []);
        setCoverage(covRes.data || null);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  // Load Knowledge Document when selectedCode changes
  useEffect(() => {
    if (!selectedCode) return;
    let cancelled = false;
    setLoadingDoc(true);
    (async () => {
      try {
        const r = await api.get(`/taxonomy/node/${encodeURIComponent(selectedCode)}/360`);
        if (!cancelled) setKnowledge(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoadingDoc(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCode]);

  // Live search (debounced)
  useEffect(() => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/taxonomy/search?q=${encodeURIComponent(query)}&limit=20`);
        setSearchResults(r.data?.items || []);
      } catch {
        setSearchResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  // Tree grouping per macro
  const treeByMacro = useMemo(() => {
    const macros = tree.filter((n) => n.level === 1);
    return macros.map((m) => ({
      macro: m,
      children: tree.filter((n) => n.parent_macro_tree === m.code && n.code !== m.code),
    }));
  }, [tree]);

  return (
    <div className="space-y-4" data-testid="taxonomy-explorer-root">
      {/* Header + coverage stats */}
      <Header coverage={coverage} />

      {/* Search bar */}
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
        <input
          data-testid="tax-explorer-search"
          type="text"
          placeholder="Buscar por código (T-04), nombre, sinónimo o keyword..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2.5 bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg text-sm
                     focus:border-[var(--ag-gold)] focus:outline-none transition-colors"
        />
        {searchResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-[var(--ag-surface)] border border-[var(--ag-border)]
                          rounded-lg shadow-2xl max-h-80 overflow-y-auto">
            {searchResults.map((r) => (
              <button
                key={r.code}
                data-testid={`tax-search-result-${r.code}`}
                onClick={() => { setSelectedCode(r.code); setSearchResults([]); setQuery(""); }}
                className="w-full text-left px-3 py-2 hover:bg-[var(--ag-surface-hover)] border-b border-[var(--ag-border)] last:border-0"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-[var(--ag-gold)]">{r.code}</span>
                  <span className="text-sm">{r.name_es || r.name?.es}</span>
                  <span className="ml-auto text-[10px] text-[var(--ag-text-dim)]">L{r.level}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Tree panel */}
        <div className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg p-3 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
            <TreeStructure size={12} /> Árbol canónico v1.2
          </div>
          {treeByMacro.length === 0 ? (
            <div className="text-xs text-[var(--ag-text-dim)]">Cargando árbol...</div>
          ) : (
            <TreeView
              groups={treeByMacro}
              expanded={expanded}
              setExpanded={setExpanded}
              selectedCode={selectedCode}
              onSelect={setSelectedCode}
            />
          )}
        </div>

        {/* Knowledge Document panel */}
        <div className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg p-4 lg:p-6 min-h-[60vh]">
          {loadingDoc ? (
            <div className="text-xs text-[var(--ag-text-dim)]">Cargando Knowledge Document...</div>
          ) : knowledge && knowledge.exists ? (
            <KnowledgeDocumentView doc={knowledge} onSelectCode={setSelectedCode} />
          ) : (
            <div className="text-sm text-[var(--ag-text-dim)]">Selecciona un nodo del árbol o busca uno.</div>
          )}
        </div>
      </div>
    </div>
  );
}


/* =================================================================== */
/* Tree subcomponent                                                    */
/* =================================================================== */

function TreeView({ groups, expanded, setExpanded, selectedCode, onSelect }) {
  const toggle = (code) => {
    const next = new Set(expanded);
    if (next.has(code)) next.delete(code); else next.add(code);
    setExpanded(next);
  };

  return (
    <div className="space-y-0.5">
      {groups.map(({ macro, children }) => {
        const isOpen = expanded.has(macro.code);
        const l2Children = children.filter((c) => c.level === 2);
        return (
          <div key={macro.code}>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => toggle(macro.code)}
                className="text-[var(--ag-text-dim)] hover:text-[var(--ag-text)] p-0.5"
                aria-label={isOpen ? "Colapsar" : "Expandir"}
              >
                {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
              </button>
              <button
                data-testid={`tax-tree-node-${macro.code}`}
                onClick={() => onSelect(macro.code)}
                className={`flex-1 text-left px-2 py-1.5 rounded text-xs hover:bg-[var(--ag-surface-hover)] transition-colors
                            ${selectedCode === macro.code ? "bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]" : ""}`}
              >
                <span className="font-mono text-[10px] opacity-70">{macro.code}</span>{" "}
                <span className="font-medium">{macro.name?.es}</span>
              </button>
            </div>
            {isOpen && l2Children.length > 0 && (
              <div className="ml-5 mt-0.5 space-y-0.5 border-l border-[var(--ag-border)] pl-2">
                {l2Children.map((c) => (
                  <button
                    key={c.code}
                    data-testid={`tax-tree-node-${c.code}`}
                    onClick={() => onSelect(c.code)}
                    className={`block w-full text-left px-2 py-1 rounded text-[11px] hover:bg-[var(--ag-surface-hover)]
                                ${selectedCode === c.code ? "bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]" : "text-[var(--ag-text-dim)]"}`}
                  >
                    <span className="font-mono text-[9px] opacity-70">{c.code}</span>{" "}
                    <span>{c.name?.es}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* =================================================================== */
/* KnowledgeDocument view subcomponent                                  */
/* =================================================================== */

function KnowledgeDocumentView({ doc, onSelectCode }) {
  // Sprint T-6 · doc can be either:
  //   - flat Knowledge Document (T-5 shape: doc.node + doc.path + doc.children + doc.relationships + doc.aliases + doc.professionals)
  //   - 360° view (T-6 shape: doc.node + doc.knowledge.* + doc.organizations + doc.catalog + doc.rag + doc.consumers)
  const isThreeSixty = !!doc.consumers;
  const k = isThreeSixty ? doc.knowledge : doc;
  const { node } = doc;
  const path = k.path;
  const parents = k.parents || [];
  const children = k.children || [];
  const relationships = k.relationships || { outgoing: [], inbound: [] };
  const aliases = k.aliases || [];
  const professionals = k.professionals || [];
  const orgs = isThreeSixty ? doc.organizations : null;
  const catalog = isThreeSixty ? doc.catalog : null;
  const rag = isThreeSixty ? doc.rag : null;
  const consumers = isThreeSixty ? doc.consumers : null;

  const name = node.name?.es;
  const desc = node.description?.es;
  const slug = node.slug?.es;
  const synonyms = Object.values(node.synonyms || {}).flat().filter(Boolean);

  return (
    <div className="space-y-4" data-testid="tax-explorer-doc">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)]">
          <Hash size={11} /> {node.code} · L{node.level} · {node.node_type}
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="tax-explorer-node-name">
          {name}
        </h2>
        {path?.breadcrumb && (
          <div className="text-[11px] text-[var(--ag-text-dim)] mt-1 font-mono">
            {path.breadcrumb}
          </div>
        )}
      </div>

      {/* Flags */}
      <div className="flex flex-wrap gap-1.5">
        {node.is_regulated && <Badge tone="warn">⚖️ Regulado</Badge>}
        {node.public_funding_available && <Badge tone="info">💰 Ayudas públicas</Badge>}
        {node.is_reservable && <Badge tone="ok">📅 Reservable</Badge>}
        {node.is_lead_gen && <Badge tone="muted">📧 Lead gen</Badge>}
        {node.needs_quote && <Badge tone="muted">💬 Presupuesto</Badge>}
        {(node.countries_enabled || []).map((c) => (
          <Badge key={c} tone="muted">🌍 {c}</Badge>
        ))}
      </div>

      {/* Description */}
      {desc && (
        <div>
          <Label>Descripción</Label>
          <p className="text-sm leading-relaxed">{desc}</p>
        </div>
      )}

      {/* Slug + CNAE */}
      <div className="grid grid-cols-2 gap-3">
        {slug && <Field label="Slug SEO" value={slug} mono />}
        {node.cnae_code && <Field label="CNAE" value={node.cnae_code} mono />}
        {node.legal_ref && <Field label="Referencia legal" value={node.legal_ref} />}
        {node.funding_program && <Field label="Programa ayuda" value={node.funding_program} />}
      </div>

      {/* Synonyms */}
      {synonyms.length > 0 && (
        <div>
          <Label>Sinónimos / aliases comerciales</Label>
          <div className="flex flex-wrap gap-1.5">
            {synonyms.slice(0, 12).map((s, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-[var(--ag-bg)] text-[var(--ag-text-dim)] border border-[var(--ag-border)]">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Children */}
      {children?.length > 0 && (
        <Section title={`Hijos directos (${children.length})`} icon={<TreeStructure size={11} />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {children.slice(0, 30).map((c) => (
              <button
                key={c.code}
                onClick={() => onSelectCode(c.code)}
                data-testid={`tax-doc-child-${c.code}`}
                className="flex items-center gap-2 text-[12px] text-left px-2 py-1 rounded hover:bg-[var(--ag-surface-hover)]"
              >
                <span className="font-mono text-[10px] text-[var(--ag-gold)]">{c.code}</span>
                <span className="truncate">{c.name?.es}</span>
                <ArrowRight size={11} className="ml-auto opacity-40" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Parents path */}
      {parents?.length > 0 && (
        <Section title="Ancestros" icon={<CaretDown size={11} />}>
          <div className="flex flex-wrap gap-1.5">
            {parents.map((p) => (
              <button
                key={p.code}
                onClick={() => onSelectCode(p.code)}
                className="text-[11px] px-2 py-1 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/40"
              >
                <span className="font-mono opacity-60">{p.code}</span> {p.name?.es}
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Relationships (Knowledge Graph) */}
      {(relationships?.outgoing?.length > 0 || relationships?.inbound?.length > 0) && (
        <Section title="Knowledge Graph · Relaciones" icon={<Graph size={11} />}>
          {(relationships.outgoing || []).map((r, i) => (
            <RelationLine
              key={`o-${i}`}
              kind={r.type}
              direction="→"
              code={r.target_code}
              name={r.target_name_es}
              note={r.notes}
              onClick={() => onSelectCode(r.target_code)}
            />
          ))}
          {(relationships.inbound || []).map((r, i) => (
            <RelationLine
              key={`i-${i}`}
              kind={r.type}
              direction="←"
              code={r.source_code}
              name={r.source_name_es}
              note={r.notes}
              onClick={() => onSelectCode(r.source_code)}
            />
          ))}
        </Section>
      )}

      {/* Aliases */}
      {aliases?.length > 0 && (
        <Section title={`Alias inbound (${aliases.length})`} icon={<LinkIcon size={11} />}>
          {aliases.map((a) => (
            <button
              key={a.source_code}
              onClick={() => onSelectCode(a.source_code)}
              className="block text-[12px] text-left hover:text-[var(--ag-gold)]"
            >
              <span className="font-mono text-[10px]">{a.source_code}</span> {a.notes}
            </button>
          ))}
        </Section>
      )}

      {/* Professionals */}
      {professionals?.length > 0 && (
        <Section title={`Profesionales aplicables (${professionals.length})`} icon={<Tag size={11} />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {professionals.map((p) => (
              <div
                key={p.code}
                className="flex items-center gap-2 text-[12px] px-2 py-1 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)]"
              >
                <span className="font-mono text-[10px] text-[var(--ag-gold)]">{p.code}</span>
                <span className="truncate">{p.name?.es}</span>
                {p.requires_certification && <span className="ml-auto text-[9px] text-amber-300">cert.</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Sprint T-6 · 360° operational panels */}
      {orgs && orgs.count > 0 && (
        <Section title={`Organizaciones usando este código (${orgs.count})`} icon={<Hash size={11} />}>
          <div className="space-y-1" data-testid="tax-360-orgs">
            {(orgs.sample || []).map((o) => (
              <a
                key={o.id}
                href={`/centro/${o.slug}`}
                target="_blank"
                rel="noreferrer"
                className="block text-[12px] px-2 py-1 rounded hover:bg-[var(--ag-surface-hover)] border border-transparent hover:border-[var(--ag-border)]"
              >
                <span className="font-medium">{o.name}</span>
                <span className="text-[var(--ag-text-dim)] ml-2">{o.city}</span>
                {o.rating_avg > 0 && (
                  <span className="ml-2 text-amber-300 text-[10px]">★ {Number(o.rating_avg).toFixed(1)}</span>
                )}
                <span className="ml-auto text-[10px] text-[var(--ag-text-dim)]">{o.account_type}</span>
              </a>
            ))}
            {orgs.count > orgs.sample.length && (
              <div className="text-[10px] text-[var(--ag-text-dim)] px-2">… y {orgs.count - orgs.sample.length} más</div>
            )}
          </div>
        </Section>
      )}

      {catalog && catalog.count > 0 && (
        <Section title={`Servicios catálogo asociados (${catalog.count})`} icon={<Tag size={11} />}>
          <div className="space-y-1" data-testid="tax-360-catalog">
            {(catalog.sample || []).map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-[12px] px-2 py-1 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)]">
                <span className="font-medium truncate">{c.name}</span>
                <span className="ml-auto text-[10px] text-[var(--ag-text-dim)]">{c.duration_min}min</span>
                {c.price > 0 && <span className="text-[10px] text-[var(--ag-gold)] ml-2">{c.price}€</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {rag && (
        <Section title="Knowledge RAG" icon={<Books size={11} />}>
          <div className="text-[11px] space-y-0.5" data-testid="tax-360-rag">
            <div><span className="text-[var(--ag-text-dim)]">code:</span> <span className="font-mono">{rag.code}</span></div>
            <div><span className="text-[var(--ag-text-dim)]">source:</span> {rag.source_type}</div>
            {rag.indexed_at && <div><span className="text-[var(--ag-text-dim)]">indexed:</span> {new Date(rag.indexed_at).toLocaleString()}</div>}
            {rag.text_length && <div><span className="text-[var(--ag-text-dim)]">size:</span> {rag.text_length} chars</div>}
            {rag.revision && <div><span className="text-[var(--ag-text-dim)]">rev:</span> <span className="font-mono">{String(rag.revision).slice(0,12)}</span></div>}
          </div>
        </Section>
      )}

      {consumers && consumers.length > 0 && (
        <Section title={`Consumers activos (${consumers.length})`} icon={<Graph size={11} />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="tax-360-consumers">
            {consumers.map((c, i) => (
              <div key={i} className="flex items-center gap-2 text-[12px] px-2 py-1 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                <span className="font-medium">{c.module}</span>
                <span className="ml-auto text-[10px] text-[var(--ag-text-dim)]">{c.count}×</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Citation footer */}
      <div className="pt-4 mt-4 border-t border-[var(--ag-border)] text-[10px] text-[var(--ag-text-dim)] font-mono">
        {doc.citation} · schema {doc.schema_version}
      </div>
    </div>
  );
}


/* =================================================================== */
/* Header + Coverage                                                    */
/* =================================================================== */

function Header({ coverage }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
          <Books size={11} weight="fill" /> Taxonomy Explorer · v1.2
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tighter mt-1">
          Canonical Knowledge Layer
        </h1>
        <p className="text-xs text-[var(--ag-text-dim)] mt-1 max-w-2xl">
          Visualiza el árbol completo de la Taxonomía oficial · {coverage?.rag?.taxonomy_nodes_total || 0} nodos ·
          {" "}{coverage?.rag?.professional_types_total || 0} profesionales · {coverage?.rag?.chunks_total || 0} chunks RAG indexados.
        </p>
      </div>
      {coverage && (
        <div className="hidden md:flex flex-col items-end gap-1 text-[10px]">
          <div className="flex items-center gap-2">
            <ChartBar size={11} />
            <span className="font-bold text-[var(--ag-gold)] text-base">
              {coverage.completion_percentage}%
            </span>
            <span className="text-[var(--ag-text-dim)]">cobertura</span>
          </div>
          <div className="text-[var(--ag-text-dim)]">
            {coverage.organizations_migrated}/{coverage.organizations_total} orgs · {coverage.catalog_migrated}/{coverage.catalog_total} servicios
          </div>
          <div className="text-[var(--ag-text-dim)]">
            {coverage.consumers_remaining_count} consumers pendientes (T-6/T-7)
          </div>
        </div>
      )}
    </div>
  );
}


/* =================================================================== */
/* Small UI atoms                                                       */
/* =================================================================== */

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-1">{children}</div>;
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className={`text-[12px] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="pt-3 border-t border-[var(--ag-border)]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] mb-2">
        {icon} {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = {
    warn: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    info: "bg-sky-500/10 text-sky-300 border-sky-500/30",
    ok: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    muted: "bg-[var(--ag-bg)] text-[var(--ag-text-dim)] border-[var(--ag-border)]",
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tones[tone] || tones.muted}`}>
      {children}
    </span>
  );
}

function RelationLine({ kind, direction, code, name, note, onClick }) {
  const kindColors = {
    alias_of: "text-purple-300",
    complementary: "text-emerald-300",
    requires: "text-rose-300",
    precedes: "text-sky-300",
    deprecated_by: "text-amber-300",
  };
  return (
    <button
      onClick={onClick}
      data-testid={`tax-doc-rel-${code}`}
      className="block w-full text-left text-[12px] px-2 py-1 rounded hover:bg-[var(--ag-surface-hover)]"
    >
      <span className={`text-[10px] font-mono mr-2 ${kindColors[kind] || "text-[var(--ag-text-dim)]"}`}>
        {kind} {direction}
      </span>
      <span className="font-mono text-[10px] text-[var(--ag-gold)] mr-1">{code}</span>
      <span>{name}</span>
      {note && <span className="text-[10px] text-[var(--ag-text-dim)] ml-2">— {note}</span>}
    </button>
  );
}
